import React from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Clock, IndianRupee, Play, Video, Check, X, Lock } from "lucide-react";
import { LAWYER_BASE } from "@lawyer/constants/route";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { STATUS_BADGE, TYPE_ICON, avatarTone, fmtAmount, fmtTime, initials, typeLabel, actionWindow, opensLabel, instantWait, EARLY_JOIN_MIN, INSTANT_WAIT_MIN } from "./consultationUi";

export function Avatar({ name, size = "h-11 w-11 text-sm" }) {
  return <div className={`${size} rounded-full grid place-items-center font-extrabold shrink-0 ${avatarTone(name)}`} data-testid="consultation-avatar">{initials(name)}</div>;
}

export function Meta({ c, className = "" }) {
  const Icon = TYPE_ICON[c.consultation_type] || Video;
  return (
    <div className={`flex items-center gap-4 text-xs font-semibold text-slate-600 ${className}`} data-testid="consultation-meta">
      <span className="inline-flex items-center gap-1.5"><Icon size={14} className="text-slate-400" /> {typeLabel(c.consultation_type)}</span>
      <span className="inline-flex items-center gap-1.5"><Clock size={14} className="text-slate-400" /> {c.duration_minutes || 15} min</span>
      <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5"><IndianRupee size={12} className="text-slate-500" /> {fmtAmount(c).slice(1)}</span>
    </div>
  );
}

export function StatusBadge({ status }) {
  const [label, cls] = STATUS_BADGE[status] || [status || "pending", "bg-slate-100 text-slate-600 border-slate-200"];
  return <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider ${cls}`} data-testid="consultation-status">{label}</span>;
}

/** Amber "Client waiting · N min left" for an unstarted instant booking; rose when the auto-close deadline has passed. */
export function InstantWaitPill({ c, now }) {
  const w = instantWait(c, now);
  if (!w) return null;
  return (
    <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider ${w.overdue ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-amber-50 text-amber-800 border-amber-200 animate-pulse"}`}
          title={`Instant bookings auto-close with a refund if not started within ${INSTANT_WAIT_MIN} minutes of payment`}
          data-testid={`instant-wait-${c.id}`} data-remaining={w.remainingMin}>
      <Clock size={11} /> {w.overdue ? "Closing · not started in time" : `Client waiting · ${w.remainingMin} min left`}
    </span>
  );
}

/** "More ▾" — secondary lifecycle actions + details (ID lives here, never in the primary row). */
export function MoreMenu({ c, items, align = "end" }) {
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button type="button" className="h-10 px-3.5 rounded-xl border border-slate-200 bg-white text-xs font-bold text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5 shrink-0" data-testid={`more-${c.id}`}>
          More <ChevronDown size={14} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="w-60 rounded-xl">
        {items.map((it) => it.to
          ? <DropdownMenuItem key={it.label} asChild><Link to={it.to} data-testid={it.testId} className="text-xs font-semibold">{it.label}</Link></DropdownMenuItem>
          : <DropdownMenuItem key={it.label} onSelect={it.onSelect} data-testid={it.testId} className={`text-xs font-semibold ${it.danger ? "text-rose-600 focus:text-rose-700" : ""}`}>{it.label}</DropdownMenuItem>)}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigator.clipboard?.writeText(c.id)} className="text-[11px] text-slate-500" data-testid={`copy-id-${c.id}`}>
          ID {(c.id || "").slice(0, 8)} · copy
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const btn = "h-10 px-4 rounded-xl text-xs font-extrabold inline-flex items-center gap-2 shrink-0 transition-colors";
const roomTo = (c) => `${LAWYER_BASE}/consultations/${c.id}/room`;

/** Primary + visible secondary actions per status; everything else goes to More. Lifecycle actions respect the slot window. */
export function actionsFor(c, { onAccept, onReject, onStart, onNoShow, inUpcoming, now = new Date(), compact = false }) {
  const paid = c.payment_status === "paid";
  const win = actionWindow(c, now);
  const more = [];
  const primary = [];
  const lockedStart = (key) => (
    <button key={key} type="button" disabled title={`You can start from ${EARLY_JOIN_MIN} minutes before the scheduled time`}
            className={`${btn} bg-slate-100 text-slate-500 cursor-not-allowed`} data-testid={`start-locked-${c.id}`}>
      <Lock size={14} /> {opensLabel(win.opensAt, now)}
    </button>
  );
  if (c.status === "pending") {
    primary.push(<button key="accept" onClick={() => onAccept(c.id)} className={`${btn} bg-emerald-600 text-white hover:bg-emerald-700`} data-testid={`accept-${c.id}`}><Check size={14} /> Accept</button>);
    if (win.canReject) primary.push(<button key="reject" onClick={() => onReject(c.id)} className={`${btn} border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100`} data-testid={`reject-${c.id}`}><X size={14} /> Reject</button>);
    if (paid && win.canStart) more.push({ label: "Start now", onSelect: () => onStart(c.id), testId: `start-${c.id}` });
  } else if (c.status === "confirmed" || c.status === "accepted") {
    if (paid && win.canStart) {
      const urgent = !!instantWait(c, now);
      primary.push(<button key="start" onClick={() => onStart(c.id)} className={`${btn} bg-[#D4AF37] text-[#0A2540] hover:bg-[#c9a430] ${urgent ? "ring-2 ring-amber-400 ring-offset-1" : ""}`} data-testid={`start-${c.id}`}><Play size={14} fill="currentColor" /> Start consultation</button>);
      more.push({ label: "Open room", to: roomTo(c), testId: `join-${c.id}` });
    } else if (win.opensAt) {
      primary.push(lockedStart("start-locked"));
    }
    if (win.canReject) more.push({ label: "Reject…", onSelect: () => onReject(c.id), testId: `reject-${c.id}`, danger: true });
  } else if (["live", "waiting_for_lawyer", "waiting_for_user"].includes(c.status)) {
    primary.push(<Link key="room" to={roomTo(c)} className={`${btn} bg-rose-600 text-white hover:bg-rose-700`} data-testid={`join-${c.id}`}><Video size={14} /> Open consultation room</Link>);
  } else if (c.status === "completed") {
    const published = c.lcr_state === "published";
    primary.push(
      <Link key="lcr" to={`${LAWYER_BASE}/consultations/${c.id}/lcr`} data-testid={`lcr-cta-${c.id}`} title={published ? "Open the published record" : "Complete Legal Consultation Record"}
            className={`${btn} h-9 whitespace-nowrap ${published ? "border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "bg-[#D4AF37] text-[#0A2540] hover:bg-[#c9a430]"}`}>
        {published ? `Record · v${c.lcr_version || 1}` : c.lcr_state === "draft" ? "Resume draft record" : compact ? "Complete LCR" : "Complete Legal Consultation Record"}
      </Link>,
    );
  }
  if (inUpcoming && (c.status === "accepted" || c.status === "confirmed" || c.status === "pending") && win.canNoShow) {
    more.push({ label: "Mark client no-show…", onSelect: () => onNoShow(c.id), testId: `noshow-${c.id}`, danger: true });
  }
  return { primary, more };
}

/** Compact booking row: Time | Client | Subject | Type · Duration · Amount | Status | Primary | More. */
export function ConsultationRow({ c, dayTitle, handlers }) {
  const { primary, more } = actionsFor(c, handlers);
  return (
    <li className="px-4 py-3 bg-white rounded-xl border border-slate-200 flex flex-wrap items-center gap-x-5 gap-y-3 hover:border-slate-300 transition-colors" data-testid={`consultation-row-${c.id}`} data-status={c.status}>
      <div className="w-24 shrink-0">
        <p className="text-base font-extrabold text-[#0A2540] leading-tight" data-testid="row-time">{fmtTime(c)}</p>
        <span className="mt-1 inline-block rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">{dayTitle}</span>
      </div>
      <Avatar name={c.user_name} />
      <div className="min-w-[160px] flex-1">
        <p className="font-extrabold text-[#0A2540] truncate" data-testid="row-client">{c.user_name || "Client"}</p>
        {c.query && <p className="text-xs text-slate-500 truncate" data-testid="row-subject">"{c.query}"</p>}
      </div>
      <Meta c={c} className="hidden md:flex" />
      <div className="flex flex-col items-start gap-1">
        <StatusBadge status={c.status} />
        <InstantWaitPill c={c} now={handlers.now} />
      </div>
      <div className="flex items-center gap-2 ml-auto">
        {primary}
        <MoreMenu c={c} items={more} />
      </div>
    </li>
  );
}
